// crewbit-admin/src/components/money/branding/InvoiceBrandingForm.tsx
//
// Brand-only invoice settings form (admin parallel of mobile
// crewbit-mobile/components/money/branding/InvoiceBrandingForm.tsx).
//
// 7 fields per spec D2:
//   1. logo_url        (file upload)
//   2. accent_color    (color picker + hex input)
//   3. invoice_prefix  (text, max 10 chars)
//   4. default_note    ("Invoice footer text" — wins over "Default note")
//   5. default_thank_you
//   6. e-Transfer membership in accepted_methods (checkbox; §Settings Phase 2B
//      retired the dedicated accept_etransfer column)
//   7. etransfer_email  (conditional)
//
// Tax / payment terms / business contact intentionally absent — those edit
// surfaces live in BootstrapWizard re-entry (Money settings landing) +
// Setup Guide "Tell us about your business" + "Set up tax" items.
//
// Per-field blur-save pattern preserves admin UX convention. Save success
// surfaces via Toast; activity_log entry per persist.

import { useEffect, useRef, useState } from 'react';
import { palette } from '../../../styles/tokens';
import { useInvoiceBranding } from '../../../hooks/useInvoiceBranding';
import { useAuthStore } from '../../../stores/authStore';
import { Toast } from '../../common/Toast';

const LABEL: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: palette.g500,
  marginBottom: 4,
  display: 'block',
};
const INPUT: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  borderRadius: 6,
  border: `1px solid ${palette.g200}`,
  fontSize: 13.5,
  color: palette.g800,
  fontFamily: 'inherit',
  outline: 'none',
  boxSizing: 'border-box' as const,
};
const ROW: React.CSSProperties = {
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  marginBottom: 12,
};

const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = new Set(['image/png', 'image/jpeg', 'image/webp', 'image/svg+xml']);

interface Props {
  businessId: string | undefined;
}

export function InvoiceBrandingForm({ businessId }: Props) {
  const user = useAuthStore((s) => s.user);
  const { settings, loading, save, uploadLogo } = useInvoiceBranding(businessId);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Controlled local state — initialized once from `settings` on first load.
  const [accentColor, setAccentColor] = useState('#6CB4EE');
  const [invoicePrefix, setInvoicePrefix] = useState('INV');
  const [defaultNote, setDefaultNote] = useState('');
  const [thankYou, setThankYou] = useState('');
  const [etransferEmail, setEtransferEmail] = useState('');
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!settings || initialized) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-shot init from async-fetched settings; guarded by `initialized` flag
    setAccentColor(settings.accent_color || '#6CB4EE');
    setInvoicePrefix(settings.invoice_prefix || 'INV');
    setDefaultNote(settings.default_note ?? '');
    setThankYou(settings.default_thank_you ?? '');
    setEtransferEmail(settings.etransfer_email ?? '');
    setInitialized(true);
  }, [settings, initialized]);

  if (loading || !settings) return null;

  const persist = async (patch: Record<string, unknown>) => {
    if (!user) return;
    setSaving(true);
    await save(patch, user.id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setUploadError(null);
    if (!ALLOWED_LOGO_TYPES.has(file.type)) {
      setUploadError('Use a PNG, JPG, WebP, or SVG image.');
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      setUploadError('Image must be 5 MB or smaller.');
      return;
    }
    setSaving(true);
    const result = await uploadLogo(file, user.id);
    setSaving(false);
    if (result === null) {
      setUploadError("Couldn't upload the logo. Try again.");
      return;
    }
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div
      style={{
        background: palette.white,
        borderRadius: 10,
        border: `1px solid ${palette.g150}`,
        overflow: 'hidden',
      }}
    >
      <Toast
        message="Invoice branding saved."
        type="success"
        isVisible={saved}
        onDismiss={() => setSaved(false)}
      />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '14px 20px',
          borderBottom: `1px solid ${palette.g100}`,
        }}
      >
        <span style={{ fontSize: 14, fontWeight: 600, color: palette.g800 }}>Invoice branding</span>
        {saving && <span style={{ fontSize: 12, color: palette.g400 }}>Saving...</span>}
      </div>
      <div style={{ padding: '16px 20px' }}>
        {/* Logo */}
        <div style={{ marginBottom: 16 }}>
          <label style={LABEL}>Logo</label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            {settings.logo_url ? (
              <img
                src={settings.logo_url}
                alt="Logo"
                style={{ height: 40, maxWidth: 120, borderRadius: 4, objectFit: 'contain' }}
              />
            ) : (
              <div
                style={{
                  width: 60,
                  height: 40,
                  borderRadius: 4,
                  background: palette.g100,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: 11,
                  color: palette.g400,
                }}
              >
                None
              </div>
            )}
            <button
              onClick={() => fileRef.current?.click()}
              style={{
                padding: '6px 12px',
                borderRadius: 6,
                border: `1px solid ${palette.g200}`,
                background: palette.white,
                fontSize: 12,
                color: palette.g600,
                cursor: 'pointer',
                fontFamily: 'inherit',
              }}
            >
              {settings.logo_url ? 'Change' : 'Upload'}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              onChange={(e) => void handleLogo(e)}
              style={{ display: 'none' }}
            />
          </div>
          {uploadError && (
            <div style={{ marginTop: 8, fontSize: 12, color: palette.red, fontWeight: 500 }}>
              {uploadError}
            </div>
          )}
        </div>

        {/* Accent + Prefix */}
        <div style={ROW}>
          <div>
            <label style={LABEL}>Accent colour</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input
                type="color"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                onBlur={() => void persist({ accent_color: accentColor })}
                style={{ width: 48, height: 32, border: 'none', cursor: 'pointer' }}
              />
              <input
                style={INPUT}
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                onBlur={() => void persist({ accent_color: accentColor })}
                placeholder="#6CB4EE"
              />
            </div>
          </div>
          <div>
            <label style={LABEL}>Invoice prefix</label>
            <input
              style={INPUT}
              value={invoicePrefix}
              onChange={(e) => setInvoicePrefix(e.target.value)}
              onBlur={() => void persist({ invoice_prefix: invoicePrefix.trim() || 'INV' })}
              placeholder="INV"
              maxLength={10}
            />
          </div>
        </div>

        {/* E-transfer — §Settings Phase 2B: derive from accepted_methods membership */}
        {(() => {
          const acceptEtransfer = settings.accepted_methods.includes('etransfer');
          return (
            <div style={{ marginBottom: 12 }}>
              <label style={{ ...LABEL, display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={acceptEtransfer}
                  onChange={(e) => {
                    const nextMethods = e.target.checked
                      ? Array.from(new Set([...settings.accepted_methods, 'etransfer']))
                      : settings.accepted_methods.filter((m) => m !== 'etransfer');
                    void persist({ accepted_methods: nextMethods });
                  }}
                />{' '}
                Accept e-Transfer payments
              </label>
              {acceptEtransfer && (
                <input
                  style={{ ...INPUT, marginTop: 6 }}
                  placeholder="payments@business.ca"
                  value={etransferEmail}
                  onChange={(e) => setEtransferEmail(e.target.value)}
                  onBlur={() => void persist({ etransfer_email: etransferEmail.trim() || null })}
                  type="email"
                />
              )}
            </div>
          );
        })()}

        {/* Footer + Thank-you */}
        <div style={{ marginBottom: 12 }}>
          <label style={LABEL}>Invoice footer text</label>
          <textarea
            style={{ ...INPUT, minHeight: 60, resize: 'vertical' }}
            value={defaultNote}
            onChange={(e) => setDefaultNote(e.target.value)}
            onBlur={() => void persist({ default_note: defaultNote.trim() })}
            placeholder="Appears at the bottom of every invoice."
          />
        </div>
        <div>
          <label style={LABEL}>Thank-you message</label>
          <textarea
            style={{ ...INPUT, minHeight: 40, resize: 'vertical' }}
            value={thankYou}
            onChange={(e) => setThankYou(e.target.value)}
            onBlur={() => void persist({ default_thank_you: thankYou.trim() })}
            placeholder="Thank you for your business!"
          />
        </div>
      </div>
    </div>
  );
}
